import React, { useState, useEffect } from 'react';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Globe,
  Target,
  Zap,
  Search,
  Clock,
  CheckSquare,
  Square
} from 'lucide-react';

// ─────────────────────────────────────────────
// SelectableList — sezione con checkbox per ogni item
// ─────────────────────────────────────────────
const SelectableList = ({ title, icon: Icon, color, items, selected, onToggle, onSelectAll, onDeselectAll, renderItem, disabled }) => {
  const selectedCount = items.filter((_, i) => selected.has(i)).length;
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${color}`} />
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          <span className="text-sm text-gray-500">({selectedCount}/{items.length} selezionati)</span>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-200">
          {!disabled && items.length > 0 && (
            <div className="flex gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100">
              <button
                onClick={onSelectAll}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
              >
                <CheckSquare className="w-3.5 h-3.5" /> Seleziona tutti
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={onDeselectAll}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 font-medium"
              >
                <Square className="w-3.5 h-3.5" /> Deseleziona tutti
              </button>
            </div>
          )}
          <div className="p-4 space-y-2">
            {items.length > 0 ? items.map((item, idx) => (
              <div
                key={idx}
                onClick={() => !disabled && onToggle(idx)}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                  selected.has(idx)
                    ? 'border-indigo-300 bg-indigo-50'
                    : 'border-gray-200 bg-gray-50 opacity-50'
                } ${disabled ? 'cursor-default' : 'cursor-pointer hover:border-indigo-400'}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {selected.has(idx)
                    ? <CheckSquare className="w-4 h-4 text-indigo-600" />
                    : <Square className="w-4 h-4 text-gray-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  {renderItem(item, idx)}
                </div>
              </div>
            )) : (
              <p className="text-gray-500 text-sm">Nessun elemento</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// ReviewGate — con logica di curation per C0
// ─────────────────────────────────────────────
const ReviewGate = ({ execution, onApprove, onReject, onRegenerate, loading, getCuratedOutput }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  const handleApprove = () => {
    const curated = getCuratedOutput();
    onApprove(curated);
  };

  if (execution.status === 'approved') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Research Universe approvato</span>
        </div>
      </div>
    );
  }

  if (execution.status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Execution rigettata</span>
        </div>
        {execution.rejectionReason && (
          <p className="text-sm text-red-600 mt-2">{execution.rejectionReason}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-800 mb-1">Approva selezione</h3>
        <p className="text-sm text-gray-500 mb-3">
          Solo i competitors, keywords e platforms selezionati verranno passati come input al Contract 1.
        </p>
        <div className="flex gap-3">
          <button
            onClick={handleApprove}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <CheckCircle2 className="w-4 h-4" />
            Approva selezione
          </button>
          <button
            onClick={onRegenerate}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            Rigenera
          </button>
          <button
            onClick={() => setShowRejectModal(true)}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            <XCircle className="w-4 h-4" />
            Rigetta
          </button>
        </div>
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Motivo del Rigetto</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Spiega perché rigetti questa execution..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={4}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Conferma Rigetto
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────
// Contract00View
// ─────────────────────────────────────────────
const Contract00View = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  const output = execution ? (execution.output || execution.outputData || {}) : {};
  const { competitors = [], keywords = [], platforms = [], discovery_metadata = {} } = output;

  const isApprovedOrRejected = execution && (execution.status === 'approved' || execution.status === 'rejected');

  // Selezione: Set di indici selezionati — tutti attivi di default
  const [selComp, setSelComp] = useState(() => new Set(competitors.map((_, i) => i)));
  const [selKw, setSelKw] = useState(() => new Set(keywords.map((_, i) => i)));
  const [selPlat, setSelPlat] = useState(() => new Set(platforms.map((_, i) => i)));

  // Resetta selezione se arriva una nuova execution
  useEffect(() => {
    setSelComp(new Set(competitors.map((_, i) => i)));
    setSelKw(new Set(keywords.map((_, i) => i)));
    setSelPlat(new Set(platforms.map((_, i) => i)));
  }, [execution?._id]);

  const toggle = (setter) => (idx) => {
    setter(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  };

  const selectAll = (items, setter) => () => setter(new Set(items.map((_, i) => i)));
  const deselectAll = (setter) => () => setter(new Set());

  const getCuratedOutput = () => {
    const included = competitors.filter((_, i) => selComp.has(i));
    const excluded = competitors.filter((_, i) => !selComp.has(i));
    return {
      competitors: included,
      included_brands: included,
      excluded_brands: excluded,
      keywords: keywords.filter((_, i) => selKw.has(i)),
      platforms: platforms.filter((_, i) => selPlat.has(i)),
      discovery_metadata
    };
  };

  if (!execution) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          <Search className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="text-lg font-medium mb-2">In attesa di esecuzione</p>
          <p className="text-sm">Clicca "Esegui Contract" per avviare il Research Universe Builder</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto p-6 space-y-6">

        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-pink-500 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1">Research Universe</h1>
              <p className="text-indigo-100 text-sm">
                Seleziona competitors, keywords e platforms da passare al Contract 1
              </p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 text-indigo-100 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm">{new Date(execution.createdAt).toLocaleDateString('it-IT')}</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                execution.status === 'completed' ? 'bg-green-500' :
                execution.status === 'approved' ? 'bg-blue-500' :
                execution.status === 'rejected' ? 'bg-red-500' : 'bg-yellow-500'
              }`}>
                {execution.status}
              </span>
            </div>
          </div>
        </div>

        {/* Stats selezione */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Competitors', total: competitors.length, sel: selComp.size, color: 'blue' },
            { label: 'Keywords',    total: keywords.length,    sel: selKw.size,   color: 'green' },
            { label: 'Platforms',   total: platforms.length,   sel: selPlat.size, color: 'purple' }
          ].map(({ label, total, sel, color }) => (
            <div key={label} className="bg-white border border-gray-200 rounded-lg p-4">
              <p className={`text-2xl font-bold text-${color}-600`}>{sel}<span className="text-sm font-normal text-gray-400">/{total}</span></p>
              <p className="text-sm text-gray-500">{label} selezionati</p>
            </div>
          ))}
        </div>

        {/* Competitors */}
        <SelectableList
          title="Competitors"
          icon={Target}
          color="text-blue-600"
          items={competitors}
          selected={selComp}
          onToggle={toggle(setSelComp)}
          onSelectAll={selectAll(competitors, setSelComp)}
          onDeselectAll={deselectAll(setSelComp)}
          disabled={isApprovedOrRejected}
          renderItem={(item) => {
            const name = typeof item === 'string' ? item : (item.name || item.brand_name || JSON.stringify(item));
            const type = typeof item === 'object' ? item.type : null;
            const positioning = typeof item === 'object' ? item.positioning : null;
            const url = typeof item === 'object' ? item.url : null;
            const handles = typeof item === 'object' && item.social_handles ? item.social_handles : null;
            return (
              <>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900 text-sm">{name}</span>
                  {url && <a href={url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-blue-600 text-xs hover:underline">Sito →</a>}
                </div>
                {type && <p className="text-xs text-gray-500 mt-0.5">{type}</p>}
                {positioning && <p className="text-xs text-gray-600 mt-0.5">{positioning}</p>}
                {handles && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {Object.entries(handles).map(([pl, h]) => (
                      <span key={pl} className="px-1.5 py-0.5 bg-white text-blue-700 rounded text-xs border border-blue-200">{pl}: {h}</span>
                    ))}
                  </div>
                )}
              </>
            );
          }}
        />

        {/* Keywords */}
        <SelectableList
          title="Keywords"
          icon={Zap}
          color="text-green-600"
          items={keywords}
          selected={selKw}
          onToggle={toggle(setSelKw)}
          onSelectAll={selectAll(keywords, setSelKw)}
          onDeselectAll={deselectAll(setSelKw)}
          disabled={isApprovedOrRejected}
          renderItem={(item) => {
            const text = typeof item === 'string' ? item : (item.keyword || item.term || item.query || JSON.stringify(item));
            const category = typeof item === 'object' ? item.category : null;
            const volume = typeof item === 'object' ? item.search_volume_estimate : null;
            const score = typeof item === 'object' ? item.relevance_score : null;
            return (
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium text-gray-900 text-sm">{text}</span>
                  {category && <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">{category}</span>}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {volume && <span>Vol: {volume}</span>}
                  {score && <span className="px-2 py-0.5 bg-white rounded font-medium text-green-700 border border-green-200">{score}/10</span>}
                </div>
              </div>
            );
          }}
        />

        {/* Platforms */}
        <SelectableList
          title="Platforms"
          icon={Globe}
          color="text-purple-600"
          items={platforms}
          selected={selPlat}
          onToggle={toggle(setSelPlat)}
          onSelectAll={selectAll(platforms, setSelPlat)}
          onDeselectAll={deselectAll(setSelPlat)}
          disabled={isApprovedOrRejected}
          renderItem={(item) => {
            const name = typeof item === 'string' ? item : (item.platform_name || item.name || item.platform || JSON.stringify(item));
            const priority = typeof item === 'object' ? item.priority : null;
            const target = typeof item === 'object' ? item.target_demographics : null;
            const formats = typeof item === 'object' && Array.isArray(item.content_formats) ? item.content_formats : [];
            return (
              <>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-900 text-sm">{name}</span>
                  {priority && (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      priority === 'high' ? 'bg-red-100 text-red-700' :
                      priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                    }`}>{priority}</span>
                  )}
                </div>
                {target && <p className="text-xs text-gray-600">{target}</p>}
                {formats.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {formats.map((f, fi) => (
                      <span key={fi} className="px-1.5 py-0.5 bg-white text-purple-700 rounded text-xs border border-purple-200">{f}</span>
                    ))}
                  </div>
                )}
              </>
            );
          }}
        />

        {/* Discovery Metadata (sempre read-only) */}
        {Object.keys(discovery_metadata).length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-4 h-4 text-indigo-500" />
              <h3 className="text-sm font-semibold text-gray-700">Discovery Metadata</h3>
            </div>
            <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto text-gray-600">
              {JSON.stringify(discovery_metadata, null, 2)}
            </pre>
          </div>
        )}

        {/* Review Gate */}
        <ReviewGate
          execution={execution}
          onApprove={onApprove}
          onReject={onReject}
          onRegenerate={onRegenerate}
          loading={loading}
          getCuratedOutput={getCuratedOutput}
        />
      </div>
    </div>
  );
};

export default Contract00View;
